import {
  Component,
  Optional,
  Inject,
  OnInit,
  forwardRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  FlatpickrDirective,
  provideFlatpickrDefaults,
  FlatpickrDefaults,
} from 'angularx-flatpickr';
import { Swedish } from 'flatpickr/dist/l10n/sv.js'; // <‑‑ importera locale

@Component({
  imports: [FlatpickrDirective, FormsModule],
  providers: [
    provideFlatpickrDefaults({
      weekNumbers: true,
      getWeek: (date: Date): string => {
        // ISO week date calculation
        const d = new Date(date.valueOf());
        const dayNum = d.getDay() || 7;
        d.setDate(d.getDate() + 4 - dayNum);
        const yearStart = new Date(d.getFullYear(), 0, 1);
        return String(Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7));
      },
      locale: Swedish,
      dateFormat: 'Y-m-d',
      altFormat: 'Y VW',
    }),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FlatpickrComponent),
      multi: true,
    },
  ],
  template: `
    <div class="input-group input-group-sm">
      <input
        #flatpickrInput
        type="text"
        mwlFlatpickr
        [(ngModel)]="selectedDate2"
        (ngModelChange)="onDateChange($event)"
        [altInput]="true"
        [convertModelValue]="true"
        [disabled]="disabled"
        class="form-control"
      />
      <span class="input-group-btn">
        <button
          class="btn btn-default"
          type="button"
          (click)="toggleCalendar()"
          [disabled]="disabled"
        >
          <i class="fa fa-calendar"></i>
        </button>
      </span>
    </div>
  `,
  styles: [
    `
      ::ng-deep .bs5-iso .input-group-sm > .form-control {
        font-size: 14px;
      }
      ::ng-deep .bs5-iso .input-group-sm > input.flatpickr-input {
        font-size: 14px;
      }
    `,
  ],
  standalone: true,
  selector: 'flatpickr-component',
})
export class FlatpickrComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @ViewChild('flatpickrInput', { static: false }) flatpickrInput!: ElementRef;

  selectedDate2: Date = new Date();
  disabled = false;
  private flatpickrInstance: any;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(@Optional() @Inject(FlatpickrDefaults) private defaults: FlatpickrDefaults) {}

  ngOnInit(): void {
    console.log('Flatpickr defaults (injected):', this.defaults);
    console.log('Swedish locale object:', Swedish);
  }

  ngAfterViewInit(): void {
    // Get reference to flatpickr instance
    if (this.flatpickrInput && this.flatpickrInput.nativeElement) {
      this.flatpickrInstance = (this.flatpickrInput.nativeElement as any)._flatpickr;
    }
  }

  toggleCalendar(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.toggle();
    }
  }

  onDateChange(date: Date): void {
    this.selectedDate2 = date;
    const dateString = this.dateToString(date);
    this.onChange(dateString);
    this.onTouched();
  }

  // Convert Date to YYYY-MM-DD string
  private dateToString(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Convert YYYY-MM-DD string to Date
  private stringToDate(dateString: string): Date | null {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value) {
      const date = this.stringToDate(value);
      if (date) {
        this.selectedDate2 = date;
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
